import dayjs from "dayjs";
import { REPORT_GAIN_TYPE_COLUMN_LABEL } from "../../../utils/reports/reportDisplayLabels";

export type FieldColumn = {
  key: string;
  value: string;
  locked?: boolean;
  minWidth?: number;
  align?: "left" | "center" | "right";
};

export const BOND_DUE_DATE_REPORT = "Due date in next 30 days";

export const BOND_TABLE_SEGMENT = "Bonds";

export const BOND_REPORT_TYPE_OPTIONS = [
  { value: "Holding", label: "Unrealized" },
  { value: "Realized", label: "Realized" },
  { value: "All", label: "All" },
  { value: "Transaction", label: "Transaction" },
  { value: BOND_DUE_DATE_REPORT, label: BOND_DUE_DATE_REPORT },
] as const;

export const BOND_QUICK_REPORTS = [
  { id: "holding", label: "Unrealized", reportType: "Holding" },
  { id: "realized", label: "Realized", reportType: "Realized" },
  { id: "all", label: "All", reportType: "All" },
  { id: "transaction", label: "Transaction", reportType: "Transaction" },
  { id: "due-date", label: BOND_DUE_DATE_REPORT, reportType: BOND_DUE_DATE_REPORT },
];

export const INVESTOR_COLUMN: FieldColumn = {
  key: "investorDisplay",
  value: "Investor Name",
  locked: true,
  minWidth: 200,
};

/** aumsampaan Bonds Holding preset (modern report panel — HOLDING_TXN_REPORT_FIELD_TITLES_BASE) */
export const BOND_UNREALIZED_HOLDING_COLUMNS: FieldColumn[] = [
  { key: "promoter", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "investedAmount", value: "Invested", minWidth: 128 },
  { key: "currentAmount", value: "Current", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPercentage", value: "P&L%", minWidth: 96 },
];

export const BOND_REALIZED_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "transactionDate", value: "Date", minWidth: 112 },
  { key: "units", value: "Units Sold", minWidth: 100 },
  { key: "purchaseNAV", value: "Sell Price", minWidth: 112 },
  { key: "avgBuyPrice", value: "Avg Buy Price", minWidth: 112 },
  { key: "saleValue", value: "Sale Value", minWidth: 128 },
  { key: "realisedPnl", value: "Realized P&L", minWidth: 132 },
  { key: "realisedPNLPercentage", value: "Realized P&L%", minWidth: 120 },
];

export const BOND_TRANSACTION_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 220 },
  { key: "transType", value: "Type", locked: true, minWidth: 120 },
  { key: "units", value: "Quantity", minWidth: 100 },
  { key: "price", value: "Price", minWidth: 112 },
  { key: "amount", value: "Amount", minWidth: 128 },
  { key: "transactionDate", value: "TransactionDate", minWidth: 132 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "settlementDate", value: "SettlementDate", minWidth: 132 },
  { key: "maturityDate", value: "MaturityDate", minWidth: 132 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "currentPrice", value: "Current Price", minWidth: 112 },
  { key: "unrealisedGainLoss", value: "P&L", minWidth: 132 },
  { key: "unrealisedGainLossPercentage", value: "P&L %", minWidth: 120 },
  { key: "days", value: "Avg. Days", minWidth: 88 },
];

export const BOND_ALL_COMMON_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "costValue", value: "Invested Amount", minWidth: 128 },
  { key: "currentValue", value: "Current Value", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPercentage", value: "P&L %", minWidth: 96 },
];

export const getColumnsForBondReportType = (reportType: string): FieldColumn[] => {
  switch (reportType) {
    case "Holding":
    case BOND_DUE_DATE_REPORT:
      return BOND_UNREALIZED_HOLDING_COLUMNS;
    case "Realized":
      return BOND_REALIZED_COLUMNS;
    case "All":
      return BOND_ALL_COMMON_COLUMNS;
    case "Transaction":
      return BOND_TRANSACTION_COLUMNS;
    default:
      return BOND_UNREALIZED_HOLDING_COLUMNS;
  }
};

export const isBondDueDateReport = (reportType: string): boolean =>
  reportType === BOND_DUE_DATE_REPORT;

export const reportTypeNeedsDateRange = (reportType: string): boolean =>
  reportType === "Holding";

export const buildDueReportDateRange = (): { startDate: number; endDate: number } => ({
  startDate: dayjs().startOf("day").valueOf(),
  endDate: dayjs().add(1, "month").endOf("day").valueOf(),
});
